/**
 * Individual block wrapper for all root level blocks
 * - Adding sections and class names
 * - Ignoring title and description blocks
 * - Rendering block header and footer
 */

import { PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import { ButtonGroup } from '@designsystem/stories/03-molecules/ButtonGroup';
import {
  Divider,
  DividerProps,
} from '@designsystem/stories/03-molecules/Divider';
import { Editable } from '../../Editable';
import RichText from '../../RichText/RichText';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import type { StyleClassNameExtenderArgs } from '@fhnw/types/blockStyle';

// Blocks rendered as section
export const sectionBlocks = [
  'imageslider',
  'group',
  'infoevent',
  'irf',
  'tabs_block',
  'slider',
  'newsletter',
  'teaser',
  'testimonial',
  'accordion',
  'schemaForm',
  'crmformblock',
  'search',
  'solarSearch',
  'listing',
  'maps',
  'keydata',
  'application',
  'contacts',
  'lecturers',
  'specializations',
  'research_focus',
  'distributor',
  'layered_content',
  'leri',
  'sub_topics',
  'cta_block',
  'locations_block',
  'modules',
];

// Blocks not to be wrapped at all
const doNotWrap = [
  'group',
  'anchor',
  'keydata',
  'application',
  'contacts',
  'lecturers',
  'specializations',
  'research_focus',
  'distributor',
  'sub_topics',
  'modules',
  'followus',
  'infinity_marquee',
];

export const styleClassNameExtender = ({
  data,
  classNames,
}: StyleClassNameExtenderArgs) => {
  const additional: string[] = [];
  const blockType = data['@type'];
  if (typeof blockType === 'string' && sectionBlocks.includes(blockType)) {
    additional.push('block--section');
  } else {
    additional.push('block--inline');
  }
  return [...classNames, ...additional];
};

export interface BlockWrapperDataProps {
  ['@type']?: string;
  block_compact?: boolean;
  block_followup?: boolean;
  block_fullbleed?: boolean;
  block_fullwidth?: boolean;
  block_hide?: boolean;
  block_title?: string | { id: string; defaultMessage: string };
  block_nav_title?: string | { id: string; defaultMessage: string };
  block_lead?: string;
  block_divider?: DividerProps & { show?: boolean };
  block_actions?: ButtonProps[];
  block_background?: 'none' | 'brand' | 'invert';
}

export interface BlockWrapperProps extends PropsWithChildren {
  data: BlockWrapperDataProps;
  block?: string;
  onChangeBlock?: (blockId: string, newData: any) => void;
  isEdit?: boolean;
}

export const BlockWrapper = (
  props: BlockWrapperProps & { factory: React.FC<BlockWrapperProps> },
) => {
  const intl = useIntl();
  const { factory: Factory, ...rest } = props;
  const { data, block, onChangeBlock, isEdit } = props;

  if (!isEdit && data.block_hide) {
    return <div data-block-type={data['@type'] as string} />;
  }

  const block_nav_title: string | undefined =
    typeof data.block_nav_title === 'object' && data.block_nav_title?.id
      ? intl.formatMessage(data.block_nav_title)
      : (data.block_nav_title as string) || undefined;
  const block_title: string | undefined =
    typeof data.block_title === 'object' && data.block_title.id
      ? intl.formatMessage(data.block_title)
      : (data.block_title as string) || undefined;

  const classNames = ['page__section'];
  let Tag: React.ElementType = 'section';
  let Heading: React.ElementType = 'h2';

  if (data.block_compact) {
    classNames.push('page__section--compact');
  }

  if (data.block_followup) {
    classNames.push('page__section--followup');
    Heading = 'h3';
  }

  if (data.block_fullbleed) {
    classNames.push('page__section--fullbleed');
  }

  if (data.block_fullwidth) {
    classNames.push('page__section--fullwidth');
  }

  if (data['@type'] && !sectionBlocks.includes(data['@type'])) {
    classNames.push('page__section--inline');
    Tag = 'div';
  }

  if (data.block_background && data.block_background !== 'none') {
    classNames.push(`surface--${data.block_background}`);
  }

  if (data['@type'] === 'newsletter') {
    classNames.push('newsletter');
  }

  const lead =
    typeof data.block_lead === 'string' ? data.block_lead.trim() : undefined;

  let title: string | ReactNode = block_title;
  if (title && isEdit) {
    title = (
      <Editable
        name="block_title"
        value={block_title as string}
        data={data}
        block={block}
        onChangeBlock={onChangeBlock}
      />
    );
  }

  return (
    <Tag
      id={block}
      className={classNames.join(' ')}
      data-nav-title={block_nav_title}
      data-block-type={data['@type'] as string}
    >
      {(block_title ||
        data.block_lead ||
        (data.block_divider && data.block_divider.show)) && (
        <div className="page__section-intro">
          {data.block_divider?.show && (
            <Divider {...(normalizeContent(data.block_divider) as object)} />
          )}
          {block_title && <Heading>{title}</Heading>}
          {lead && (
            <p>
              <RichText text={lead} />
            </p>
          )}
        </div>
      )}
      <div className="page__section-content">
        <Factory {...rest} />
      </div>
      {data.block_actions && (
        <div className="page__section-footer">
          <ButtonGroup
            spaced={true}
            items={normalizeContent(data.block_actions) as ButtonProps[]}
          />
        </div>
      )}
    </Tag>
  );
};

const blockWrapperFactory = (
  Factory: React.FC<BlockWrapperProps>,
  isEdit: boolean,
) => {
  return (props: Omit<BlockWrapperProps, 'factory'>) => {
    if (props?.data?.['@type'] && doNotWrap.includes(props?.data?.['@type'])) {
      return <Factory {...props} />;
    }
    return <BlockWrapper isEdit={isEdit} factory={Factory} {...props} />;
  };
};

export default blockWrapperFactory;
